<template>
  <div class="container" :class="platformSimulatedClass" :dir="uiDirection">
    <div v-show="!isChatLoaded" class="appLoader">
      <div class="loaderContainer">
        <div class="loader"></div>
        <div>
          <h4>{{ $t('loader_title') }}</h4>
          <span>{{ $t('loader_description') }}</span>
        </div>
      </div>
    </div>
    <div v-show="isChatLoaded && isStartBtnDisplayed" class="webchat-start-container">
      <div class="webchat-preview-cta">
        <h5>{{ $t('test_application') }}</h5>
        <a
          href="#"
          class="btn btn-primary m-btn m-btn--custom m-btn--icon"
          data-wizard-action="next"
          @click.prevent="startBtnClick">
          <span>
            <span class="startBtn">{{ $t('btn_start') }}</span>
            &nbsp;&nbsp;
          </span>
        </a>
      </div>
    </div>
    <!-- Conversation hub for hamburger menu mode -->
    <ConversationHub
      v-if="showHamburgerConversationHub"
      :conversation-hub="conversationHub"
      :avatar="headerAvatar"
      :assistant-title="title"
      :assistant-id="assistantId"
      :is-kiosk="isKiosk"
      @resume-conversation="handleShowHamburgerConversationHub"
      @conversation-click="selectConversation" />
    <div v-else v-show="isChatLoaded && !isStartBtnDisplayed" class="chat-container">
      <div class="header" :style="headerStyle">
        <a v-if="isHamburgerHubEnabled" class="hub-button" @click="handleShowHamburgerConversationHub">
          <i class="fas fa-bars" aria-hidden="true"></i>
        </a>
        <img :src="headerAvatar" />
        <h2 v-if="isTitleVisible">{{ title }}</h2>
        <header-shortcut-menu
          v-if="areHeaderShortcutsDisplayed"
          :menu-items="headerShortcutMenu"
          :is-kiosk="isKiosk"
          :assistant-id="assistantId"
          :is-disabled="isSendingMessage || isSTTBusy" />
        <!-- v-show is prefered to v-if here because it cause issue in element rendering order or voice icon html elements -->
        <!-- isTTSEnabled is for admin mode or areAudioOptionsVisible -->
        <div class="controllers-container">
          <div v-show="shouldShowVolumeIcons" class="icon-container">
            <a id="voice-speaking" class="voice-speaking" href="#" @click.stop.prevent="enableTTS(false)">
              <i class="fa fa-volume-off"></i>
              <i class="fa fa-volume-down"></i>
              <i class="fa fa-volume"></i>
              <i class="fa fa-volume-up"></i>
            </a>
            <a id="voice-deactivated" href="#" class="voice-deactivated" @click.stop.prevent="enableTTS(true)">
              <i class="fa fa-volume-mute"></i>
            </a>
            <a id="voice-activated" href="#" class="voice-activated" @click.stop.prevent="enableTTS(false)">
              <i class="fa fa-volume"></i>
            </a>
          </div>
          <div v-if="isAdmin || (isYeldaWebDomain && areAudioOptionsVisible)" class="icon-container">
            <a href="#" class="cog-wheel" @click.stop.prevent="toggleMenu"><i id="cog_wheel" class="fa fa-cog"></i></a>
          </div>
          <div v-if="canBeClosed" class="icon-container">
            <a href="#" :style="{ color: headerStyle.color }" class="webchat-close" @click.prevent="closeChat">
              <i class="fa fa-times"></i>
            </a>
          </div>
        </div>
      </div>
      <!-- Conversation hub for tabs mode -->
      <ConversationTabs
        v-if="isTabHubEnabled"
        :active-tab="activeTab"
        :conversation-hub="conversationHub"
        :is-conversation-active="true"
        @tab-change="setActiveTab" />

      <!--
        - Admins options are visible only in admin mode (app.yelda.ai) or areAudioOptionsVisible
        - isShowLogs is reserved for admin mode
        - isSTTContinuous is reserved for admin mode or yelda Hosted webpage (web.yelda.ai) && areAudioOptionsVisible
      -->
      <div v-if="showDropdown" v-click-outside="outsideClick" class="dropdown-container">
        <!-- NLP details are only activated for admin mode -->
        <div v-if="isAdmin" class="dropdown-item">
          <label for="isShowLogs" class="switch">
            <span>{{ $t('admin_infos') }}</span>
            <input id="isShowLogs" v-model="isShowLogs" class="toggle" type="checkbox" />
          </label>
        </div>
        <!-- Continuous recording is only activated for admin mode or Yelda Hosted webpage with audio activated -->
        <div v-if="isSTTEnabled" class="dropdown-item">
          <label for="isSTTEnabled" class="switch">
            <span>{{ $t('admin_stt_continuous') }}</span>
            <input id="isSTTEnabled" v-model="isSTTContinuous" class="toggle" type="checkbox" />
          </label>
        </div>
        <div class="line-break"></div>
        <div class="dropdown-item">
          <span>{{ $t('admin_new_session') }}</span>
          <br />
          <a href="#" class="btn" @click.prevent="startNewConversation">
            <span>
              {{ $t('admin_start_returning_user') }}
            </span>
          </a>
          <a href="#" class="btn" @click.prevent="startAsNewUser">
            <span>
              {{ $t('admin_start_new_user') }}
            </span>
          </a>
        </div>
        <div v-if="user && user.firstName" class="line-break"></div>
        <div v-if="user && user.firstName" class="dropdown-item">
          <span>{{ $t('admin_user_info', { userName: `${user.firstName} ${user.lastName}` }) }}</span>
        </div>
        <div v-if="hasConversationContextList" class="line-break"></div>
        <div v-if="hasConversationContextList" class="dropdown-item">
          <table width="100%" cellspacing="0" cellpadding="5" class="contextLogger">
            <thead>
              <th>{{ $t('context_table.header.title') }}</th>
              <th>{{ $t('context_table.header.value') }}</th>
            </thead>
            <tbody>
              <tr v-for="(context, index) in conversationContextList" :key="index">
                <td>{{ getContextLabel(context, locale) }}</td>
                <td>{{ getContextValue(context) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <!-- Conversation hub for tabs mode -->
      <ConversationHub
        v-if="showTabsConversationHub"
        :conversation-hub="conversationHub"
        :avatar="headerAvatar"
        :assistant-title="title"
        :display-tabs="false"
        :assistant-id="assistantId"
        :is-kiosk="isKiosk"
        @resume-conversation="() => setActiveTab(tabs.ACTIVE_CONVERSATION)"
        @conversation-click="selectConversation" />
      <div id="chatBox" class="chat-box">
        <div id="chatList" class="chatList" @scroll.passive="handleScroll()">
          <div v-if="isShownConvBeginning && !selectedConversation" class="chatWindowMsg convBeginning">
            <p>{{ $t('conversation_beginning', { title: title }) }}</p>
          </div>
          <div v-if="isShownLoader && !selectedConversation" class="conversationLoader">
            <div></div>
            <div></div>
            <div></div>
          </div>
          <div v-if="isShownLoadBtn && !selectedConversation" class="loadMoreBtn">
            <a href class="btn" :style="{ color: bubbleBgColor }" @click.prevent="loadMoreConversation()">
              {{ $t('load_more_conversation') }}
            </a>
          </div>
          <template v-for="(message, index) in messagesToDisplay">
            <div
              v-if="
                isLocalStorageEnabled && message.isStartOfConversation && !isFreshConversation && !selectedConversation
              "
              :key="`conversation_${index}`"
              class="conversationLineBreak">
              {{ $t('conversation_title', { number: message.conversationNumber }) }} -
              {{ formatDate(message.sentTime, locale, $t, false) }}
            </div>
            <!-- Container for message or chip -->
            <div
              v-if="isValidMessage(message) && shouldRenderMessage(message)"
              :key="index"
              :class="[
                isClientMessage(message.origin) ? 'right-img' : 'left-img',
                isMessageFormat(message.messageType) ? 'textContainer' : '',
                isChipFormat(message.messageType) ? 'chipsContainer' : '',
                isCarouselFormat(message.messageType) ? 'carouselContainer' : '',
                isLastOfSerie(messagesToDisplay, index) ? 'last-of-serie' : '',
                isAdminMessage(message) ? 'message-agent' : '',
                'message-box'
              ]">
              <div
                v-if="isShowAvatar(message.messageType)"
                class="picture"
                :style="
                  isClientMessage(message.origin) && {
                    color: bubbleBgColor
                  }
                ">
                <img :src="profileImage(message.origin, message.adminId)" />
              </div>
              <div v-if="isFileFormat(message.messageType)" class="messageFile">
                <File
                  :element-style="isClientMessage(message.origin) && bubblesStyle"
                  :message="message"
                  :intent-analysed="message.intentAnalysed"
                  :action-deduced="message.actionDeduced"
                  :context="message.context"
                  :is-show-logs="isShowLogs"
                  :assistant-id="assistantId"
                  :assistant-slug="assistantSlug" />
              </div>
              <div v-if="isMessageFormat(message.messageType)" class="messageText">
                <Message
                  :element-style="isClientMessage(message.origin) && bubblesStyle"
                  :message="message"
                  :intent-analysed="message.intentAnalysed"
                  :action-deduced="message.actionDeduced"
                  :context="message.context"
                  :is-show-logs="isShowLogs"
                  :assistant-id="assistantId"
                  :assistant-slug="assistantSlug"
                  :streaming-gate-id="streamingGateId"
                  @openUrl="openUrl"
                  @stream-start="handleStreamStart"
                  @stream-end="handleStreamEnd" />
              </div>
              <div
                v-else-if="isChipFormat(message.messageType) && !shouldDelayRichMessage(message)"
                class="messageChip">
                <Chips
                  :element-style="bubblesStyle"
                  :message="message"
                  :assistant-id="assistantId"
                  :is-admin="isAdmin"
                  :is-kiosk="isKiosk" />
              </div>
              <div
                v-else-if="isCarouselFormat(message.messageType) && !shouldDelayRichMessage(message)"
                class="messageCarousel">
                <CarouselItems
                  :element-style="bubblesStyle"
                  :message="message"
                  :is-navigation-enabled="isNavigationEnabled(message)"
                  :is-disabled="message.isClickDisabled"
                  :is-admin="isAdmin"
                  :is-kiosk="isKiosk" />
              </div>
              <div
                v-else-if="isFormFormat(message.messageType) && !shouldDelayRichMessage(message)"
                class="formContainer">
                <Form :message="message" :is-disabled="message.form.isFieldsDisabled" :is-admin="isAdmin" />
              </div>
            </div>
          </template>
          <div
            v-if="webkitSTTMessage && webkitSTTMessage !== ''"
            class="last-of-serie textContainer right-img continues message-box">
            <div
              class="picture"
              :style="{
                color: bubbleBgColor
              }">
              <img :src="profileImage('client')" />
            </div>
            <div class="messageText">
              <Message
                :element-style="bubblesStyle"
                :message="{ mainContent: webkitSTTMessage }"
                :assistant-id="assistantId"
                :assistant-slug="assistantSlug" />
            </div>
          </div>
          <div v-if="isShowingTypeIndicator" class="last-of-serie message-box left-img indicatorContainer">
            <div class="picture">
              <img :src="headerAvatar" />
            </div>
            <TypeIndicator />
          </div>
          <div v-if="!connected" class="chatWindowMsg errorMsg">
            <p>
              {{ $t('server_down_message') }}
              <a v-if="shouldDisplayReloadButton" @click.stop.prevent="reloadWebchat">{{ $t('reload_assistant') }}</a>
            </p>
          </div>
          <div v-if="errorMessage" class="chatWindowMsg errorMsg">
            <p>{{ errorMessage }}</p>
          </div>
          <div v-if="selectedConversation && !selectedConversation.isConversationActive" class="conversationLineBreak">
            {{ $t('conversation_ended_message', { date: formatDate(selectedConversation.endTime, locale, $t) }) }}
          </div>
        </div>
      </div>
      <RatingPanel
        v-if="shouldDisplayRatingPanel && !selectedConversation"
        :last-message-id="lastMessage._id"
        :should-display-panel="shouldDisplayRatingPanel"
        @hidePanel="shouldDisplayRatingPanel = false" />
      <ChatInput
        v-if="!selectedConversation && activeTab === tabs.ACTIVE_CONVERSATION"
        :is-s-t-t-enabled="isSTTEnabled"
        :is-s-t-t-continuous="isSTTContinuous"
        :is-admin="isAdmin"
        :is-audio-recording-visible="shouldAllowAudioRecording"
        :is-audio-mobile-front="isAudioMobileFront"
        :is-audio-desktop-front="isAudioDesktopFront"
        :is-rating-panel-displayed="shouldDisplayRatingPanel"
        :is-kiosk="isKiosk"
        :is-android-web-view="isAndroidWebView"
        :user-agent-details="userAgentDetails"
        @hideRatingPanel="shouldDisplayRatingPanel = false"
        @setTTSStatus="setTTSStatus" />
    </div>
  </div>
</template>

<script>
// Lib
import audioLib from './lib/audio'
import {
  formatDate,
  scrollToNewMessage,
  getWebchatSlots,
  getMessagesFromConversations,
  getNormalizedMessages
} from './lib/utils'
import { getUserAgentObject } from './lib/ua'
import { getItem, setItem } from './lib/localStorage'
import { getPopinStatusFromLocalStorage, isWindowClosed, applyViewport } from './lib/window'
import pushNotificationLib from './lib/pushNotification'
import { extractClientPostMessageData, mergeWithStateWebchatSlots } from './lib/slot'
import Bus from './Bus'

// Dependencies
import { mapGetters } from 'vuex'

// Components
import ChatInput from './components/ChatInput.vue'
import Chips from './components/templates/Chips.vue'
import Message from './components/templates/Message.vue'
import CarouselItems from './components/templates/CarouselItems.vue'
import TypeIndicator from './components/templates/TypeIndicator.vue'
import Form from './components/templates/Form.vue'
import RatingPanel from './components/templates/RatingPanel/Index.vue'
import File from './components/templates/File.vue'
import ConversationHub from './components/ConversationHub/index.vue'
import ConversationTabs from './components/ConversationHub/ConversationTabs.vue'
import HeaderShortcutMenu from './components/HeaderShortcutMenu.vue'

// Config
import config from './config'
import hubConfig from './config/conversationHub'
import ratingConfig from './config/rating'

/**
 * Yelda Webchat
 *  - Connection flow:
 *   1. SocketIo send 'connected' event when the handshake with backend is done
 *   2. 'connected' watcher send 'socket_chatLoad' event
 *   3. On 'socket_chatLoad' response we set 'isChatLoaded' to true
 *   - If initChatStatus() has already been called (shouldBeOpened = true or localStorage value) :
 *   4. 'isChatLoaded' watcher event run initChatStatus(), 'openChat' event is sent to parent
 */
export default {
  name: 'App',
  components: {
    ChatInput: ChatInput,
    Chips: Chips,
    Message: Message,
    CarouselItems: CarouselItems,
    TypeIndicator: TypeIndicator,
    Form: Form,
    RatingPanel,
    File,
    ConversationHub,
    ConversationTabs,
    HeaderShortcutMenu
  },
  props: {
    isAdmin: { type: Boolean, default: false },
    isRTL: { type: Boolean, default: false },
    shouldBeOpened: { type: Boolean, default: false },
    canBeClosed: { type: Boolean, default: true },
    assistantId: { type: String, required: true },
    assistantSlug: { type: String, required: true },
    isStartBtn: { type: Boolean, default: false },
    pushNotificationWaitTime: { type: Number, required: true },
    isYeldaWebDomain: { type: Boolean, default: false },
    yparam: { type: String, default: null },
    platformSimulated: { type: String, default: null },
    isKiosk: { type: Boolean, default: false },
    isWebView: { type: Boolean, default: false },
    isAndroidWebView: { type: Boolean, default: false }
  },
  data() {
    return {
      isShowLogs: false,
      isTTSEnabled: false,
      isStartBtnDisplayed: false,
      isSTTEnabled: false,
      isSTTContinuous: this.isKiosk,
      isInitChatStatusQueued: false,
      isAlreadyOpened: false,
      shouldDisplayRatingPanel: false,
      timeoutRatingPanel: null,
      lastPushNotification: null,
      showDropdown: false,
      isLocalStorageEnabled: true,
      userLogo: 'https://yelda-chat.s3.eu-west-3.amazonaws.com/images/default-2.jpg',
      messageListenerBind: null,
      userAgentDetails: {},
      showHamburgerConversationHub: false,
      selectedConversation: null,
      activeTab: hubConfig.TABS.ACTIVE_CONVERSATION,
      tabs: hubConfig.TABS,
      activeStreamingId: null,
      streamedMessageIds: []
    }
  },
  computed: {
    ...mapGetters({
      isNewMessage: 'isNewMessage',
      messages: 'messages',
      hasMoreConversations: 'hasMoreConversations',
      lastScrollHeight: 'lastScrollHeight',
      idOldestConversation: 'idOldestConversation',
      popinStatus: 'popinStatus',
      isChatLoaded: 'isChatLoaded',
      nbNewMessage: 'nbNewMessage',
      isShowingTypeIndicator: 'isShowingTypeIndicator',
      title: 'title',
      isShownLoader: 'isShownLoader',
      isShownConvBeginning: 'isShownConvBeginning',
      isFreshConversation: 'isFreshConversation',
      isShownLoadBtn: 'isShownLoadBtn',
      connected: 'connected',
      avatar: 'avatar',
      style: 'style',
      pushNotifications: 'pushNotifications',
      areAudioOptionsVisible: 'areAudioOptionsVisible',
      streamWebchatMessage: 'streamWebchatMessage',
      isAudioRecordingVisible: 'isAudioRecordingVisible',
      isAudioMobileFront: 'isAudioMobileFront',
      isAudioDesktopFront: 'isAudioDesktopFront',
      webkitSTTMessage: 'webkitSTTMessage',
      endConversationActions: 'endConversationActions',
      localizedAcknowledgementTexts: 'localizedAcknowledgementTexts',
      defaultDisplayDelayRatingPanel: 'defaultDisplayDelayRatingPanel',
      actionsIgnoredForRatings: 'actionsIgnoredForRatings',
      user: 'user',
      locale: 'locale',
      isSTTBusy: 'isSTTBusy',
      isSendingMessage: 'isSendingMessage',
      isDemo: 'isDemo',
      conversationContextList: 'conversationContextList',
      shouldDisplayReloadButton: 'shouldDisplayReloadButton',
      errorMessage: 'errorMessage',
      liveChatAgent: 'liveChatAgent',
      timeZone: 'timeZone',
      parentUrl: 'parentUrl',
      isAudioPlaying: 'isAudioPlaying',
      conversationHub: 'conversationHub',
      postMessageSlotsProperties: 'postMessageSlotsProperties',
      earlyPostMessageEvents: 'earlyPostMessageEvents',
      webchatSlots: 'webchatSlots',
      headerShortcutMenu: 'headerShortcutMenu'
    }),
    hasConversationContextList() {
      return this.conversationContextList && this.conversationContextList.length
    },
    bubbleBgColor() {
      return (this.bubblesStyle && this.bubblesStyle.background) || false
    },
    bubblesStyle() {
      return (this.style && this.style.bubbles) || false
    },
    headerStyle() {
      // if no colors set for header
      if (!this.style || !this.style.header) {
        return {}
      }

      const gradientStart = this.style.header.gradientStart
      const gradientEnd = this.style.header.gradientEnd

      // set default background color
      let backgroundColor
      if (!gradientStart || !gradientEnd) {
        backgroundColor = gradientStart || gradientEnd
      }

      // only set gradient if both gradientStart and gradientEnd exist
      const shouldSetGradient = gradientStart !== null && gradientEnd !== null

      return {
        /* eslint-disable no-dupe-keys */
        ...(backgroundColor && { background: backgroundColor }),
        ...(shouldSetGradient && {
          background: `-moz-linear-gradient(-45deg, ${gradientStart} 0%, ${gradientEnd} 100%)`,
          background: `-webkit-linear-gradient(-45deg, ${gradientStart} 0%, ${gradientEnd} 100%)`,
          background: `linear-gradient(135deg, ${gradientStart} 0%, ${gradientEnd} 100%)`,
          filter: `progid:DXImageTransform.Microsoft.gradient( startColorstr='${gradientStart}', endColorstr='${gradientEnd}',GradientType=1)`
        }),
        ...(this.style.header.color && { color: this.style.header.color })
      }
    },
    headerAvatar: function () {
      return this.getServerSideAvatar(this.liveChatAgent)
    },
    lastMessage() {
      if (!this.messagesToDisplay || !this.messagesToDisplay.length) {
        return {}
      }
      // Reverse a copy of messages array to get last messages first
      const reversedOrderMessages = this.messagesToDisplay.slice().reverse()

      // Try to get last message of type 'message'
      return (
        reversedOrderMessages.find(message => message.messageType === this.$getConst('MESSAGE_TYPES.MESSAGE')) || {}
      )
    },
    isEndOfConversation() {
      return (
        this.lastMessage &&
        this.lastMessage.actionDeduced &&
        this.lastMessage.origin === config.ORIGIN_TYPES.SERVER &&
        this.endConversationActions.includes(this.lastMessage.actionDeduced.action)
      )
    },
    platformSimulatedClass() {
      return this.platformSimulated ? `${this.platformSimulated}_container` : ''
    },
    shouldShowVolumeIcons() {
      return this.isKiosk || this.isAdmin || this.areAudioOptionsVisible
    },
    shouldAllowAudioRecording() {
      return this.isKiosk || this.isAudioRecordingVisible
    },
    isConversationHubEnabled() {
      // Conversation Hub is enabled only if isActivated and has at least one tab to show
      return (
        this.conversationHub &&
        this.conversationHub.isActivated &&
        (this.conversationHub.information.menuTitle || this.conversationHub.conversationHistory.menuTitle)
      )
    },
    isHamburgerHubEnabled() {
      // Hamburger menu mode is enabled only if hasHamburgerMenu is true and conversation hub is enabled
      return this.isConversationHubEnabled && this.conversationHub.hasHamburgerMenu
    },
    isTabHubEnabled() {
      // Tab mode is enabled only if hasHamburgerMenu is false and conversation hub is enabled
      return this.isConversationHubEnabled && !this.conversationHub.hasHamburgerMenu
    },
    messagesToDisplay() {
      return this.selectedConversation ? this.formatMessages(this.selectedConversation.interactions) : this.messages
    },
    // Id of the next message allowed to stream.
    // While activeStreamingId is set, we keep the gate on that message.
    // Once it finishes, we advance the gate to the next unstreamed message.
    streamingGateId() {
      if (this.activeStreamingId) {
        return this.activeStreamingId
      }

      if (this.selectedConversation) {
        return null
      }

      return this.getNextStreamableId()
    },
    showTabsConversationHub() {
      // In tab mode, tabs buttons are always visible
      // Active conversation or old selected conversation are directly computed in this App.vue file
      // so the trick is :
      // - Hide ConversationHub component when active conversation or old selected conversation is selected
      // - Show ConversationHub component when information or conversation history tab is selected
      return this.isTabHubEnabled && this.activeTab !== this.tabs.ACTIVE_CONVERSATION && !this.selectedConversation
    },
    isTitleVisible() {
      return !this.isHamburgerHubEnabled || this.isTabHubEnabled
    },
    uiDirection() {
      return this.isRTL ? config.UI_DIRECTIONS.RTL : config.UI_DIRECTIONS.LTR
    },
    areHeaderShortcutsDisplayed() {
      return this.headerShortcutMenu && this.headerShortcutMenu.length
    }
  },
  watch: {
    isNewMessage: function () {
      if (this.isNewMessage === true) {
        scrollToNewMessage()
        this.$store.dispatch('after_new_message')

        if (this.popinStatus === this.$getConst('POPIN_STATUS.CLOSED')) {
          this.$store.dispatch('set_not_seen_status', this.nbNewMessage)
        } else {
          this.$store.dispatch('set_not_seen_status', 0)
        }

        // Hide rating panel on new message
        this.resetConversationRatingPanel()

        /*
        Don't trigger rating if action is:
        - in progress
        - included in actionsIgnoredForRatings, like the welcome action
        - a push notification
        */
        if (
          this.lastMessage &&
          this.lastMessage.actionDeduced &&
          (this.lastMessage.actionDeduced.inProgress ||
            this.actionsIgnoredForRatings.includes(this.lastMessage.actionDeduced.action) ||
            (this.lastPushNotification &&
              this.lastPushNotification.actionId === this.lastMessage.actionDeduced.actionId))
        ) {
          // Reset last push notification
          this.lastPushNotification = null
          return
        }

        // This is the end of the conversation
        // For live chat conversation do not display rating panel
        if (this.isEndOfConversation && !this.liveChatAgent) {
          this.timeoutRatingPanel = setTimeout(() => {
            this.shouldDisplayRatingPanel = true
          }, ratingConfig.DISPLAY_PANEL_DELAY.END_CONVERSATION)
          return
        }

        /* Ensure that:
          - the last message is from the server
          - no setTimeout has already been triggered
          - the default display delay exists and is not equal to 0
          - For live chat conversation do not display rating panel
        */
        if (
          this.lastMessage &&
          this.lastMessage.origin === config.ORIGIN_TYPES.SERVER &&
          !this.timeoutRatingPanel &&
          this.defaultDisplayDelayRatingPanel &&
          !this.liveChatAgent
        ) {
          this.timeoutRatingPanel = setTimeout(() => {
            this.shouldDisplayRatingPanel = true
          }, this.defaultDisplayDelayRatingPanel)
        }
      }
    },
    messages: function (nextMessages) {
      if (!nextMessages || !nextMessages.length) {
        this.resetStreamedMessageIds()
      }
    },
    isShowLogs: function () {
      setItem('isShowLogs', this.isShowLogs)
    },
    isTTSEnabled: function () {
      setItem('isTTSEnabled', this.isTTSEnabled)
      this.$store.dispatch('set_tts_status', this.isTTSEnabled)
      if (!this.isTTSEnabled) {
        // We need to let the audio know that the class to apply is deactivated
        audioLib.stop(this.isTTSEnabled)
      }
    },
    isAudioRecordingVisible: function (val) {
      this.initAdminOptions()
    },
    areAudioOptionsVisible: function (val) {
      this.initAdminOptions()
    },
    connected: function () {
      if (this.connected && !this.isChatLoaded) {
        this.$store.dispatch('socket_chatLoad')

        if (!this.isStartBtnDisplayed) {
          this.$store.dispatch('focus_input')
        }
      }
    },
    isChatLoaded: function (newValue, oldValue) {
      if (newValue !== oldValue && newValue && this.isInitChatStatusQueued) {
        this.initChatStatus()

        setTimeout(() => {
          this.$store.dispatch('set_scroll_height')
          scrollToNewMessage()
        }, 500)
      }

      if (newValue) {
        // Initialize push notification tracking
        pushNotificationLib.initPushNotification(
          this.pushNotifications,
          this.assistantId,
          this.timeZone,
          this.pushNotificationWaitTime,
          this.$store.getters
        )
      }
    },
    webkitSTTMessage: function (value) {
      if (value !== '') {
        scrollToNewMessage()
      }
    },
    timeoutRatingPanel: function (value) {
      if (value && this.isSTTBusy) {
        this.resetConversationRatingPanel()
      }
    },
    isSendingMessage: function (value) {
      parent.postMessage({ event: 'isSendingMessage', data: value })
    },
    errorMessage: function () {
      // Hide the error message after some delay
      if (this.errorMessage) {
        setTimeout(() => {
          this.$store.dispatch('set_error_message', null)
        }, config.ERROR_MESSAGE_DELAY)
      }
    },
    parentUrl: function () {
      // On parent url update check for inactivity type notification
      pushNotificationLib.checkAndReInitiateInactivityNotification(this.$store.getters)
    },
    title: function (newValue) {
      this.setSiteTitleAndAvatar(newValue, this.avatar)
    },
    avatar: function (newValue) {
      this.setSiteTitleAndAvatar(this.title, newValue)
    },
    showHamburgerConversationHub: function (newValue, oldValue) {
      // Limit the update only when closing the hub
      if (oldValue === true && newValue === false) {
        // Wait for DOM update before initializing admin options
        this.$nextTick(() => {
          this.initAdminOptions()
        })
      }
    }
  },
  mounted() {
    /*
     On iOS, we need the following logic to resize the viewport when the keyboard is shown
     so the header and input are always visible
    */
    window.visualViewport.addEventListener('resize', applyViewport)
    window.visualViewport.addEventListener('scroll', applyViewport)
    window.addEventListener('load', applyViewport)
  },
  created: function () {
    this.attachEventListener(true)
    this.attachEventListener()
    const webchatSlots = getWebchatSlots(this.yparam)
    this.$store.dispatch('set_webchat_slots', webchatSlots)
    // If we need to show the start button, don't start the chat
    if (this.isStartBtn && this.isAdmin) {
      this.isStartBtnDisplayed = true
    } else {
      this.initChatStatus()
    }

    this.initAdminOptions()

    // Check localStorage enabled or not by trying to get locale from localStorage
    this.isLocalStorageEnabled = !!getItem('locale', false)

    Bus.$on('triggerPushNotification', this.triggerPushNotification)

    // Ask chat-plugin to listen for url update so we can update the parentUrl
    parent.postMessage(config.FRAME_EVENT_TYPES.SENT.LISTEN_URL_UPDATE, '*')

    this.setSiteTitleAndAvatar(this.title, this.avatar)
  },
  beforeDestroy() {
    this.attachEventListener(true)
    Bus.$off('triggerPushNotification')
    window.visualViewport.removeEventListener('resize', applyViewport)
    window.visualViewport.removeEventListener('scroll', applyViewport)
    window.removeEventListener('load', applyViewport)
  },
  methods: {
    formatDate,
    setSiteTitleAndAvatar(title, avatar) {
      if (title) {
        document.title = title
      }
      if (avatar) {
        let link = document.querySelector("link[rel~='icon']")
        if (!link) {
          link = document.createElement('link')
          link.rel = 'icon'
          document.getElementsByTagName('head')[0].appendChild(link)
        }
        link.href = avatar
      }
    },

    handleClientPostMessageEvent(eventData) {
      // Chat:load has not been received or processed yet, so we store the event data that we will proceed on chat:load
      if (!this.postMessageSlotsProperties) {
        this.$store.dispatch('set_early_post_message_events', [...this.earlyPostMessageEvents, eventData])
        return
      }

      // if postMessageSlotsProperties is empty, we received postMessageSlots from chat:load
      // but there is no valid postMessage slot defined on yelda side, so we stop here
      if (!this.postMessageSlotsProperties.length) {
        return
      }

      const postMessageSlots = extractClientPostMessageData(this.postMessageSlotsProperties, eventData, this.locale)

      // if no postMessageSlots have been extracted, we stop here
      if (!Object.keys(postMessageSlots).length) {
        return
      }
      const updatedSlots = mergeWithStateWebchatSlots(this.webchatSlots || [], postMessageSlots)

      this.$store.dispatch('set_webchat_slots', updatedSlots)
    },
    eventListenerMethod(event) {
      // yeldaChat instance (from yeldaWebchatInjector.js) emit contentWindow.postMessage('openChat', '*')
      // event.message is there to support i11 browser now edge support event.data
      const eventData = event.data || event.message

      if (eventData === config.FRAME_EVENT_TYPES.RECEIVED.OPEN_CHAT) {
        this.handleOpenChatEven()
        return
      }

      // If eventData exists and it doesn't have an event property then we assume it's is for fill from post message type slot
      try {
        // VIDEO_XHR_CANDIDATE is hotfix to avoid spammy behavior from ohmy events
        if (
          eventData &&
          typeof eventData === 'object' &&
          !eventData.event &&
          eventData.type !== 'VIDEO_XHR_CANDIDATE'
        ) {
          this.handleClientPostMessageEvent(eventData)
          return
        }
      } catch (err) {
        console.error('Error parsing eventData:', err)
        return
      }

      // We don't have other simple string event left, so let's return
      if (!eventData || !eventData.event) {
        return
      }

      /**
          This event is triggered when the webchat receive a message that should be sent as a user message
          So let's format it and send it if:
          - event.data.data is the correct format for a message which is a string
          - a message is not currently being sent
        */
      if (this.isSendUserMessageEventValid(eventData) && !this.isSendingMessage) {
        this.handleSendUserMessageEvent()
        return
      }

      /**
       * Leave port event sent from chat plugin when the mouse leaves the viewport
       */
      if (eventData.event === config.FRAME_EVENT_TYPES.RECEIVED.LEAVE_VIEWPORT) {
        this.handleLeaveViewportEvent()
        return
      }

      /**
       * URL update event sent from chat plugin parent frame url is updated
       */
      if (eventData.event === config.FRAME_EVENT_TYPES.RECEIVED.URL_UPDATE) {
        this.$store.dispatch('set_parent_url', eventData.data)
        return
      }
    },
    /**
     * Handle openChat event triggered from chat-plugin
     */
    handleOpenChatEven() {
      // If the openChat event is triggered by a notification
      // We don't want to send an chat:open event: no need of welcome message !
      if (!this.isAlreadyOpened) {
        this.isAlreadyOpened = true
        // chat:open event adds the open event on yelda side, so separate track event is not necessary
        this.$store.dispatch('socket_chatOpen')
      } else {
        // when window opens call the track event
        this.trackWebchatEvent(config.EVENT_TYPES.OPEN)
      }

      // Set store & localStorage popinStatus to opened
      this.setPopinStatus(this.$getConst('POPIN_STATUS.OPENED'))
      // Focus the input element if exists
      this.$store.dispatch('focus_input')
    },
    /**
     * Handles send user message event triggered from chat-plugin
     */
    handleSendUserMessageEvent() {
      const message = [
        {
          messageType: config.MESSAGE_TYPES.MESSAGE,
          mainContent: event.data.data,
          inputType: config.INPUT_TYPES.TOUCH,
          intentAnalysed: null,
          actionDeduced: null,
          context: null
        }
      ]
      this.$store.dispatch('socket_userMessage', message)
    },
    /**
     * Handle leave viewport event triggered from chat-plugin
     */
    handleLeaveViewportEvent() {
      // remove the mouseout event listener
      parent.postMessage(config.FRAME_EVENT_TYPES.SENT.ABSTAIN_LEAVE_VIEWPORT, '*')
      this.pushNotifications.forEach(pushNotification => {
        if (
          pushNotification.triggerType === config.TRIGGER_TYPES.LEAVE_VIEWPORT &&
          pushNotificationLib.isParentUrlInListOfUrls(this.parentUrl, pushNotification.urls)
        ) {
          this.triggerPushNotification(pushNotification)
        }
      })
    },
    /**
     * Check sendUserMessage event is valid
     */
    isSendUserMessageEventValid(eventData) {
      return (
        eventData.event === config.FRAME_EVENT_TYPES.RECEIVED.SEND_USER_MESSAGE &&
        eventData.data &&
        typeof eventData.data === 'string'
      )
    },
    /**
     * Event listener will handle chat opening (via click on chat icon)
     */
    attachEventListener: function (remove = false) {
      // Check for right event listener method/message
      const isOldIEVersion = !window.addEventListener

      const addEventMethod = isOldIEVersion ? 'attachEvent' : 'addEventListener'
      const removeEventMethod = isOldIEVersion ? 'detachEvent' : 'removeEventListener'
      const messageEvent = isOldIEVersion ? 'onmessage' : 'message'

      const eventHandler = window[remove ? removeEventMethod : addEventMethod]

      if (!this.messageListenerBind) {
        this.messageListenerBind = this.eventListenerMethod.bind(this)
      }
      eventHandler(messageEvent, this.messageListenerBind)
    },
    trackWebchatEvent(eventType) {
      // Send tracking info for the click on link
      const data = [
        {
          event: {
            eventType,
            element: config.EVENT_ELEMENTS.WEBCHAT_POPIN
          },
          inputType: config.INPUT_TYPES.TOUCH
        }
      ]
      this.$store.dispatch('socket_userTracking', data)
    },
    /**
     * Set the popin status in state & localStorage
     */
    setPopinStatus: function (popinStatus) {
      this.$store.dispatch('set_popin_status', popinStatus)
      if (this.assistantId) {
        setItem(`popin_status_${this.assistantId}`, popinStatus)
      }
    },
    /**
     * Triggered when a user click on the start button
     */
    startBtnClick: function () {
      audioLib.ensureInit(this.isTTSEnabled)

      // If the button has been pressed, the chat is obviously open
      this.setPopinStatus(this.$getConst('POPIN_STATUS.OPENED'))

      this.isStartBtnDisplayed = false
      this.$store.dispatch('focus_input')
      this.initChatStatus()
    },
    /**
     * Toggle isTTSEnabled value and set setVoiceClass
     */
    enableTTS(isTTSEnabled) {
      this.isTTSEnabled = isTTSEnabled
      audioLib.setVoiceClass(false, this.isTTSEnabled, this.isKiosk || this.areAudioOptionsVisible || this.isAdmin)
    },
    /**
     * Start the chat if it should be opened (param or localStorage)
     */
    initChatStatus: function () {
      if (!this.isChatLoaded) {
        this.isInitChatStatusQueued = true
        return
      }

      // If the webchat cannot be closed, we force the popin status to opened
      // otherwise the chat can be considered as closed even if it is opened in the UI (consequence: no welcome message sent)
      const popinStatus = this.canBeClosed ? this.getPopinInitStatus() : this.$getConst('POPIN_STATUS.OPENED')

      // Set popin status in state & localStorage
      this.setPopinStatus(popinStatus)

      // Open the popin on load if CPS localStorage or shouldBeOpened are true
      if (popinStatus === this.$getConst('POPIN_STATUS.OPENED')) {
        parent.postMessage(config.FRAME_EVENT_TYPES.SENT.OPEN_CHAT, '*')
      }
    },
    /**
     * Set logs, voice and audio boolean depending on context
     */
    initAdminOptions() {
      const isVoiceOn = this.isKiosk || this.isAdmin || this.areAudioOptionsVisible
      const isRecordingOnInSettings = this.isKiosk || this.isAdmin || this.isAudioRecordingVisible
      this.userAgentDetails = getUserAgentObject()
      this.isShowLogs = this.isAdmin && getItem('isShowLogs', 'false') === 'true'
      this.isTTSEnabled = isVoiceOn && getItem('isTTSEnabled', 'false') !== 'false'
      /*
       Since in FB/Messenger Webview browser webkitSpeechRecognition are not working
       We hide the recording button

       Safari supports webkitSpeechRecognition but its recognition is not working well
       and has a delay of at least 8 seconds on iOS to detect the beginning of the speech
       So we forced isSTTEnabled to false so we can use recording + our STT endpoint instead

       Other iOS browsers (Chrome, Firefox, Edge) should also use the recording method
       because they don't support webkitSpeechRecognition, so let's force isSTTEnabled to false as well
      */
      this.isSTTEnabled =
        audioLib.checkMicrophoneHeaderPermissionGiven() &&
        isRecordingOnInSettings &&
        'webkitSpeechRecognition' in window &&
        !this.userAgentDetails.isFBBrowser &&
        !this.userAgentDetails.isSafari &&
        !this.userAgentDetails.isIOSBrowser

      audioLib.setVoiceClass(false, this.isTTSEnabled, isVoiceOn)
    },
    /**
     * Return if yparam value force the webchat to be opened
     * @return {String} yparam
     * @return {Boolean}
     */
    doesParentUrlForceWebchatToBeOpened: function (yparam) {
      return yparam && yparam === config.YPARAM_SHOW_BOT
    },

    /**
     * Get the popin opening status from localStorage or URL settings
     * @return {String|false} know popin status or false
     */
    getPopinInitStatus: function () {
      // If demo page, force que chat to be opened on chat init
      // if the parent of the iframe contains yparam=yshowbot param then open the webchat window on page load
      if (this.isDemo || this.doesParentUrlForceWebchatToBeOpened(this.yparam)) {
        return this.$getConst('POPIN_STATUS.OPENED')
      }

      // Regardless of the previous user's local storage choice,
      // if we are integrated in a webview and shouldBeOpened is true,
      // we force the webchat to opened status to ensure users receive their welcome message
      if (this.isWebView && this.shouldBeOpened) {
        return this.$getConst('POPIN_STATUS.OPENED')
      }

      // shouldBeOpened define the default comportment of the popin
      return (
        getPopinStatusFromLocalStorage(this.assistantId) ||
        (this.shouldBeOpened ? this.$getConst('POPIN_STATUS.OPENED') : this.$getConst('POPIN_STATUS.CLOSED'))
      )
    },

    getServerSideAvatar(adminObject) {
      // if liveChatAgent is set in the store, it means we are in isLiveChatRequested mode
      // so we return the admin image (personal admin image or default one)
      // else we use the standard assistant avatar
      return adminObject ? this.getAdminImage(adminObject) : this.avatar
    },
    getAdminImage(adminObject) {
      return (adminObject && adminObject.avatar) || config.DEFAULT_ADMIN_IMAGE
    },
    profileImage: function (origin, adminId = null) {
      if (origin === config.ORIGIN_TYPES.CLIENT) {
        return this.userLogo
      }

      return this.getServerSideAvatar(adminId)
    },

    stopAudioIfPlaying: function () {
      // Stop audio if playing
      if (this.isAudioPlaying) {
        audioLib.stop()
      }
    },
    closeChat: function () {
      this.stopAudioIfPlaying()
      parent.postMessage(config.FRAME_EVENT_TYPES.SENT.CLOSE_CHAT, '*')
      this.setPopinStatus(this.$getConst('POPIN_STATUS.CLOSED'))

      this.trackWebchatEvent(config.EVENT_TYPES.CLOSE)
    },

    isFileFormat: function (messageType) {
      return messageType === this.$getConst('MESSAGE_TYPES.FILE')
    },

    isMessageFormat: function (messageType) {
      return (
        messageType === this.$getConst('MESSAGE_TYPES.MESSAGE') ||
        messageType === this.$getConst('MESSAGE_TYPES.CAROUSEL_MESSAGE') ||
        messageType === this.$getConst('MESSAGE_TYPES.FORM_MESSAGE')
      )
    },

    isChipFormat: function (messageType) {
      return (
        messageType === this.$getConst('MESSAGE_TYPES.CHIP') ||
        messageType === this.$getConst('MESSAGE_TYPES.CAROUSEL_CHIP')
      )
    },

    isCarouselFormat: function (messageType) {
      return messageType === this.$getConst('MESSAGE_TYPES.CAROUSEL_ITEM')
    },

    isFormFormat: function (messageType) {
      return messageType === this.$getConst('MESSAGE_TYPES.FORM_ITEM')
    },

    isShowAvatar: function (messageType) {
      return (
        messageType === this.$getConst('MESSAGE_TYPES.CAROUSEL_MESSAGE') ||
        messageType === this.$getConst('MESSAGE_TYPES.MESSAGE') ||
        messageType === this.$getConst('MESSAGE_TYPES.FORM_MESSAGE') ||
        messageType === this.$getConst('MESSAGE_TYPES.FORM_ITEM') ||
        messageType === this.$getConst('MESSAGE_TYPES.FORM_CHIP')
      )
    },
    /**
     * Checks if the message should be displayed in the UI.
     * Some interactions, like the push notifications, can have event type message
     * we should not display them in empty container to avoid duplicate start of conversation
     */
    isValidMessage(message) {
      if (message.event) {
        return false
      }

      // MESSAGE_TYPES.FORM_CHIP showed inside Form.vue component, so no need to show it again
      return message.messageType !== this.$getConst('MESSAGE_TYPES.FORM_CHIP')
    },
    isClientMessage: function (messageOrigin) {
      return messageOrigin === this.$getConst('ORIGIN_TYPES.CLIENT')
    },
    getStreamMessageId(message) {
      return message && (message._id || message.sentTime)
    },
    isStreamableMessage(message) {
      if (!message || !message.mainContent || message.isStreamingDisabled) {
        return false
      }

      if (!this.streamWebchatMessage) {
        return false
      }

      if (!message.isNewMessage) {
        return false
      }

      return this.isServerMessageOrigin(message.origin)
    },
    isServerMessageOrigin(messageOrigin) {
      const serverOrigin = this.$getConst('ORIGIN_TYPES.SERVER')
      // messageOrigin can be a string ('server'/'client') or a numeric key (0/1).
      // ORIGIN_TYPES maps 0 -> 'server' and 1 -> 'client', but does not map 'server' -> 'server'.
      // So we must handle both forms to reliably detect server-origin messages.
      return messageOrigin === serverOrigin || this.$getConst(`ORIGIN_TYPES.${messageOrigin}`) === serverOrigin
    },
    shouldRenderMessage(message) {
      return !this.hasPendingStreamBefore(message)
    },
    shouldDelayRichMessage(message) {
      if (!this.hasPendingStreamBefore(message)) {
        return false
      }

      if (!message || !message.isNewMessage) {
        return false
      }

      return true
    },
    getMessageIndex(message) {
      return this.messagesToDisplay ? this.messagesToDisplay.indexOf(message) : -1
    },
    getNextStreamableId(fromIndex = 0, toIndex = null) {
      const messages = this.messagesToDisplay || []
      const limit = toIndex === null ? messages.length : toIndex

      for (let index = fromIndex; index < limit; index += 1) {
        const message = messages[index]
        if (!this.isStreamableMessage(message)) {
          continue
        }

        const streamId = this.getStreamMessageId(message)
        if (streamId && !this.streamedMessageIds.includes(streamId)) {
          return streamId
        }
      }

      return null
    },
    hasPendingStreamBefore(message) {
      const messageIndex = this.getMessageIndex(message)
      if (messageIndex <= 0) {
        return false
      }

      return !!this.getNextStreamableId(0, messageIndex)
    },
    resetStreamedMessageIds() {
      this.activeStreamingId = null
      this.streamedMessageIds = []
    },
    handleStreamStart(streamId) {
      if (!streamId) {
        return
      }

      this.activeStreamingId = streamId
    },
    handleStreamEnd(streamId) {
      if (!streamId) {
        this.activeStreamingId = null
        return
      }

      if (!this.streamedMessageIds.includes(streamId)) {
        this.streamedMessageIds.push(streamId)
      }

      if (this.activeStreamingId === streamId) {
        this.activeStreamingId = null
      }

      this.$nextTick(() => {
        scrollToNewMessage()
      })
    },

    handleScroll: function () {
      if (document.getElementById('chatList').scrollTop === 0 && this.hasMoreConversations) {
        this.$store.dispatch('set_has_more_conversations', false)
        this.$store.dispatch('socket_conversationGet', this.idOldestConversation)
      }
    },

    loadMoreConversation: function () {
      this.$store.dispatch('set_is_show_load_btn', false)
      this.$store.dispatch('socket_conversationGet', this.idOldestConversation)
    },

    isAdminMessage: function (message) {
      return 'adminId' in message
    },
    isLastOfSerie: function (messages, currentIndex) {
      if (messages.length === currentIndex + 1) {
        return true
      }

      // Then we check if the +1 type is from the same origin (bot or user)
      if (messages[currentIndex].origin !== messages[currentIndex + 1].origin) {
        return true
      }

      // special condition to check a combinaison of text and chips messages
      if (
        messages.length > currentIndex + 2 &&
        this.isMessageFormat(messages[currentIndex].messageType) &&
        this.isChipFormat(messages[currentIndex + 1].messageType) &&
        this.isMessageFormat(messages[currentIndex + 2].messageType) &&
        messages[currentIndex].origin === messages[currentIndex + 2].origin
      ) {
        return false
      }

      if (
        this.isChipFormat(messages[currentIndex + 1].messageType) ||
        this.isCarouselFormat(messages[currentIndex + 1].messageType)
      ) {
        return true
      } else {
        return false
      }
    },

    isNavigationEnabled: function (message) {
      // Enable Navigation on carousel if more than one item presents
      return message.carouselItems && message.carouselItems.length > this.$getConst('NB_MIN_CAROUSEL')
    },

    /**
     * check last message received time and call sendPushNotification
     * @param {Object} pushNotification
     * @param {String} pushNotification._id
     * @param {String} pushNotification.triggerType
     */
    triggerPushNotification: function (pushNotification) {
      // Don't send push notification if action is in progress
      if (this.lastMessage && this.lastMessage.actionDeduced && this.lastMessage.actionDeduced.inProgress) {
        return
      }

      this.lastPushNotification = pushNotification
      this.sendPushNotification(pushNotification)
    },

    /**
     * Send the pushNotification event to the socket server
     */
    sendPushNotification: function (pushNotification) {
      // Emit the notification event
      this.$store.dispatch('socket_chatPush', pushNotification).then(() => {
        // We don't need to open the webchat if it's already opened
        if (
          isWindowClosed(this.assistantId) &&
          pushNotification.style === this.$getConst('NOTIFICATION_STYLES.NORMAL')
        ) {
          this.isAlreadyOpened = true
          parent.postMessage(config.FRAME_EVENT_TYPES.SENT.OPEN_CHAT, '*')
        }

        // Remove the notification from the list of notification we are checking as we're done for today
        const pushNotifications = this.pushNotifications.filter(notification => {
          return notification._id !== pushNotification._id
        })

        pushNotificationLib.setPushNotifications(pushNotifications)
        this.$store.dispatch('set_push_notifications', pushNotifications)
        pushNotificationLib.checkAndClearTimeTrackingNotificationInterval()
        pushNotificationLib.checkAndReInitiateInactivityNotification(this.$store.getters)
      })
    },

    openUrl(url) {
      if (!url) {
        return
      }
      // Event sent to app.yelda.ai
      parent.postMessage({ event: config.FRAME_EVENT_TYPES.SENT.OPEN_URL, data: url })
    },
    toggleMenu(event) {
      event.target.classList.toggle('active')
      this.showDropdown = !this.showDropdown
    },
    outsideClick() {
      document.getElementById('cog_wheel').classList.remove('active')
      this.showDropdown = false
    },
    startAsNewUser() {
      // Set store & localStorage popinStatus to opened
      this.setPopinStatus(this.$getConst('POPIN_STATUS.OPENED'))

      this.$store.dispatch('start_as_new_user')
    },
    startNewConversation() {
      this.showDropdown = false
      document.getElementById('cog_wheel').classList.remove('active')

      // Getting the lastConversationId to check any intermediate conversation happened on other tabs
      const lastWebchatConversationId =
        this.messages.length && this.messages[this.messages.length - 1]
          ? this.messages[this.messages.length - 1].conversationId
          : null

      this.$store.dispatch('set_is_fresh_conversation', false)
      this.resetStreamedMessageIds()
      this.$store.dispatch('socket_chatOpen', { isReset: true, lastWebchatConversationId })
    },
    resetConversationRatingPanel() {
      this.shouldDisplayRatingPanel = false

      if (this.timeoutRatingPanel) {
        clearTimeout(this.timeoutRatingPanel)
        this.timeoutRatingPanel = null
      }
    },
    getContextLabel(context, locale) {
      // if context is number of visits then return the i18n label
      if (context.slug === config.NB_SESSIONS && !context.localizedNames) {
        return this.$t(`context_table.title.${context.slug}`)
      }

      // if no localizedNames then return context slot slug
      return context.localizedNames[locale] || context.slug
    },
    getContextValue(context, locale) {
      if (context.slotExtractionSource && context.slotExtractionSource === config.BOOLEAN_SLOT_TYPE) {
        return context.slotValue === config.REFUSAL_VALUE
          ? this.$t(`context_table.value.no`)
          : this.$t(`context_table.value.yes`)
      }

      return context.slotValue
    },
    reloadWebchat() {
      location.reload()
    },
    setTTSStatus(isTTSEnabled) {
      this.isTTSEnabled = isTTSEnabled
      audioLib.setVoiceClass(false, this.isTTSEnabled, this.isKiosk || this.areAudioOptionsVisible || this.isAdmin)
    },
    handleShowHamburgerConversationHub() {
      // If the conversation hub is currently visible, it means that we are closing it
      // so let's reset the selected conversation to resume the main conversation
      if (this.showHamburgerConversationHub) {
        this.selectedConversation = null
      }
      this.showHamburgerConversationHub = !this.showHamburgerConversationHub
    },
    selectConversation(conv) {
      if (this.isMostRecentConversation(conv)) {
        // If the most recent conversation is selected,
        // we deselect to go back to active conversation
        this.selectedConversation = null
        if (this.isTabHubEnabled) {
          // In tab mode, switch back to active conversation tab
          this.setActiveTab(this.tabs.ACTIVE_CONVERSATION)
          return
        }
        // In hamburger mode, we close the conversation hub
        this.showHamburgerConversationHub = false
        return
      }
      // If we select an old conversation
      this.selectedConversation = conv
      if (!this.isTabHubEnabled) {
        // In hamburger mode, we close the hub to display the selected conversation
        this.showHamburgerConversationHub = false
      }
    },
    isMostRecentConversation(conv) {
      return conv._id && this.messages.length > 0 && this.messages[this.messages.length - 1].conversationId === conv._id
    },
    formatMessages(interactions) {
      return getNormalizedMessages(getMessagesFromConversations(interactions))
    },
    setActiveTab(tab) {
      if (tab === this.activeTab) {
        if (tab === this.tabs.CONVERSATIONS) {
          // Clicking again on the conversations history tab
          // will bring the user back to the list of conversations
          this.selectedConversation = null
        }
        return
      }
      this.activeTab = tab
      this.$store.dispatch('set_selected_hub_tab', tab)
      // When switching from a different tab, we reset the selected conversation
      this.selectedConversation = null
    }
  }
}
</script>
